// src/tabs/messages/InboxComposer.jsx — BUG 38 (multi-canal) sur base Phase 2.3 (ADR-026)
//
// Composer pour répondre dans un thread Inbox, désormais MULTI-CANAL :
//   - cases à cocher rendues depuis src/lib/inbox-channels.js (registre miroir).
//   - OTA (tchat_ota) + Mail (email) + SMS (Twilio) cochables ; WhatsApp grisé
//     SANS promesse (enabled:false — canal NON retenu, décision Dimitri WAGON N).
//   - SMS : canal RÉEL côté serveur, inertie via MESSAGES_DRY_RUN (= mail) ;
//     cochable dès qu'un guest_phone existe. Coordonnée envoyée via `guestPhone`.
//   - ≥1 canal requis (bouton désactivé si 0 coché).
//   - présélection auto par source résa (airbnb/booking → OTA ; direct → Mail,
//     SMS si pas d'email mais un téléphone), raffinée par coordonnée disponible
//     (ne pré-coche pas un canal sans sa coordonnée). Modifiable par Dimitri.
//   - GATING PLATEFORME : résa airbnb/booking (variants V1 booking_com /
//     bookingdotcom via normalizeSource) → SEUL Chat OTA est proposé (alias
//     OTA inutilisables en Mail/SMS). Direct / hors-plateforme / fil sans résa
//     → toutes les cases (comportement inchangé).
//
// Envoi via /api/send-email (type='inbox_reply'), payload `canaux: [...]` (array).
// Le backend boucle PAR canal (DRY_RUN-gaté, zéro fallback) et renvoie
// { results: [{canal, status, reason?}], dryRun }. Feedback affiché par canal.
//
// Rétrocompat : la prop `canal` (string, ex. depuis ResaMessageComposerWrapper)
// reste acceptée → utilisée comme présélection si fournie.

import { useState, useMemo, useRef } from "react";
import { Reply, Sparkles, AlertTriangle, Pause, Check, Send, CheckCircle2, Circle, Ban, XCircle, Info } from "lucide-react";
import { supabase } from "../../supabase";
import { DS } from "../../lib/ui";
import { INBOX_CHANNELS, isInboxActionable } from "../../lib/inbox-channels";
import { reasonLabelFR } from "../../lib/inbox-escalation-labels";
import { normalizeSource } from "../../lib/payment-derivation";

// Gating canaux par source résa — une conversation liée à une résa PLATEFORME
// (airbnb / booking, variants V1 booking_com / bookingdotcom couverts par
// normalizeSource) ne propose QUE le Chat OTA : les coordonnées email/téléphone
// côté OTA sont des alias relais inutilisables en envoi direct (Mail/SMS
// partiraient dans le vide). Conversations direct / hors-plateforme / fil sans
// résa liée → registre COMPLET, comportement inchangé.
export function isPlatformSource(rawSource) {
  const s = normalizeSource(rawSource);
  return s === "airbnb" || s === "booking";
}

// Canaux visibles dans le composer pour cette résa (null/undefined = fil sans
// résa → tous les canaux, l'email entrant pur reste joignable via lastInbound).
export function visibleInboxChannels(reservation) {
  if (reservation && isPlatformSource(reservation.source)) {
    return INBOX_CHANNELS.filter((c) => c.id === "tchat_ota");
  }
  return INBOX_CHANNELS;
}

// Statut → rendu visuel du feedback par canal (icône Lucide trait fin, DA).
const STATUS_RENDER = {
  sent: { Icon: CheckCircle2, label: "envoyé", color: DS.success },
  sent_dry_run: { Icon: Circle, label: "simulé (mode essai)", color: DS.warning },
  skipped_no_coordinate: {
    Icon: Ban,
    label: "coordonnée absente",
    color: DS.warning,
  },
  skipped_disabled: {
    Icon: Ban,
    label: "canal indisponible",
    color: DS.warning,
  },
  failed: { Icon: XCircle, label: "échec", color: DS.danger },
};

// Coordonnée dispo pour un canal donné, selon son coordinateField + la résa.
function hasCoordinate(channel, reservation, lastInbound) {
  if (!reservation && !lastInbound) return false;
  switch (channel.coordinateField) {
    case "beds24_booking_id":
      return Boolean(reservation?.beds24_booking_id);
    case "guest_email":
      return Boolean(lastInbound?.sender_email || reservation?.guest_email);
    case "guest_phone":
      return Boolean(reservation?.guest_phone);
    default:
      return false;
  }
}

// Présélection par défaut : source résa → canal privilégié, raffiné coordonnée.
//
// WAGON N — règle simple documentée (ordre de préférence par source) :
//   - OTA (airbnb/booking)   → Chat OTA, puis Mail, puis SMS.
//   - direct / autre         → Mail, puis Chat OTA, puis SMS.
//   Le SMS n'est donc JAMAIS pré-coché tant qu'un canal écrit (OTA/Mail) est
//   joignable ; il sert de repli quand seul un téléphone est connu (ex. résa
//   directe sans email). Il reste toujours cochable manuellement par Dimitri.
function defaultSelection({ reservation, lastInbound, presetCanal }) {
  const isOta = isPlatformSource(reservation?.source);
  // Gating plateforme : la présélection ne pioche QUE dans les canaux visibles
  // (résa OTA → Chat OTA seul ; direct / fil sans résa → registre complet).
  const channels = visibleInboxChannels(reservation);
  // Ordre de préférence selon la source (SMS en repli de fin de liste).
  const preferred = presetCanal
    ? [presetCanal]
    : isOta
      ? ["tchat_ota", "email", "sms"]
      : ["email", "tchat_ota", "sms"];

  // Pré-coche UN SEUL canal : le 1er de l'ordre de préférence (selon la source)
  // qui est actionnable ET joignable. WAGON N : SMS est actionnable même si
  // enabled:false (isInboxActionable). Dimitri peut en cocher d'autres ensuite.
  const selected = {};
  const pick = preferred
    .map((id) => channels.find((c) => c.id === id))
    .find(
      (c) =>
        c &&
        isInboxActionable(c.id) &&
        hasCoordinate(c, reservation, lastInbound),
    );
  if (pick) {
    selected[pick.id] = true;
    return selected;
  }
  // Fallback : aucun canal préféré joignable → 1er canal actionnable+joignable
  // (évite un composer vide quand la coordonnée préférée manque).
  const fallback = channels.find(
    (c) =>
      isInboxActionable(c.id) && hasCoordinate(c, reservation, lastInbound),
  );
  if (fallback) selected[fallback.id] = true;
  return selected;
}

export default function InboxComposer({
  threadId,
  canal,
  reservation,
  canReply,
  lastInbound,
  onSent,
  pricingBlockId = null,
  source = null,
}) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sentAt, setSentAt] = useState(null);
  const [results, setResults] = useState(null);
  // Canaux en ÉCHEC au dernier envoi : le texte est conservé et seuls ces
  // canaux restent cochés — « Renvoyer » les relance d'un seul geste.
  const [canauxEnEchec, setCanauxEnEchec] = useState([]);
  // Verrou SYNCHRONE contre le double clic : `sending` est un état React, il
  // n'est vu qu'au rendu suivant ; une ref l'est tout de suite.
  const envoiEnCoursRef = useRef(false);
  // CLÉ D'ENVOI (29/09, point 3) : une par message composé. Le même texte
  // renvoyé (réponse perdue, ou « Renvoyer » après un échec) garde sa clé : le
  // serveur réserve l'envoi par cette clé et ne le traite qu'une fois par canal.
  // Un texte différent = une nouvelle clé.
  const cleEnvoiRef = useRef(null);

  // Chantier 12 — Copilote IA : génère un BROUILLON ÉPHÉMÈRE dans le composer
  // (jamais envoyé automatiquement — l'humain valide TOUT). Scope serveur
  // lc1/lc2/lc3. LOT 1 : l'endpoint renvoie désormais une SORTIE UNIQUE
  //   { ok, draft:<string|null>, escalated:<bool>, reason_code:<string|null>,
  //     capturable:<bool>, access_locked:<bool> }.
  // - escalated     : le copilote a escaladé → banner « à traiter par toi ».
  // - draft null    : soit escaladé sans brouillon, soit préambule bloqué
  //                   (escalated=false → simple invite à réessayer).
  // - capturable    : info absente du livret → pointeur capture FAQ (onglet IA).
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  // { escalated:bool, reasonCode:string|null, capturable:bool, hasDraft:bool }
  // hasDraft permet de distinguer « escaladé sans brouillon » de « brouillon
  // proposé malgré l'escalade » (résa inactive / sujet sensible : draft fourni).
  const [escalation, setEscalation] = useState(null);
  // Préambule bloqué (escalated=false && draft null) → invite à réessayer.
  const [preambleBlocked, setPreambleBlocked] = useState(false);
  // LOT C (ADR-248) — copilote désactivé pour ce logement (flag ia_enabled off).
  const [iaDisabled, setIaDisabled] = useState(false);
  // LOT D (ADR-249) — dernier brouillon IA proposé (ref, ≠ state qui se vide).
  // Renvoyé au serveur à l'envoi pour la capture par correction (si édité).
  // null si l'humain n'a jamais cliqué « Suggérer » (rien à comparer).
  const iaDraftRef = useRef(null);

  async function suggest() {
    setSuggesting(true);
    setSuggestError(null);
    setEscalation(null);
    setPreambleBlocked(false);
    setIaDisabled(false);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setSuggestError("Pas de session admin active");
        setSuggesting(false);
        return;
      }
      const payload = reservation?.id
        ? { reservation_id: reservation.id }
        : { thread_id: threadId };
      const res = await fetch(
        "/api/beds24-mapping?action=inbox_suggest_reply",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "inbox_suggest_reply", ...payload }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      // draft null → le champ reste vide, l'admin rédige (comportement conservé).
      const hasDraft = Boolean(json.draft);
      setBody(json.draft || "");
      // LOT D — mémorise le brouillon proposé pour la capture par correction.
      // Un re-suggest écrase (on garde le dernier draft). null si pas de draft.
      iaDraftRef.current = json.draft || null;
      if (json.ia_enabled === false) {
        // LOT C — logement non éligible (flag OFF) : réessayer ne produira rien.
        setIaDisabled(true);
      } else if (json.escalated) {
        setEscalation({
          escalated: true,
          reasonCode: json.reason_code || null,
          capturable: Boolean(json.capturable),
          hasDraft,
        });
      } else if (!hasDraft) {
        // Pas d'escalade ET pas de brouillon = préambule méta bloqué côté serveur.
        setPreambleBlocked(true);
      }
    } catch (e) {
      setSuggestError(e.message);
    } finally {
      setSuggesting(false);
    }
  }

  const initialSelection = useMemo(
    () => defaultSelection({ reservation, lastInbound, presetCanal: canal }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [selected, setSelected] = useState(initialSelection);

  // Défense en profondeur (QA M1) : l'état `selected` est figé au mount
  // (initialSelection) alors que `reservation` peut changer SANS remount
  // (ex. refresh qui lie une résa plateforme à un fil email pur — le composer
  // est keyé par thread_id, pas par résa). On intersecte donc TOUJOURS avec
  // les canaux visibles : un canal masqué par le gating plateforme ne peut
  // ni activer le bouton Envoyer ni partir dans le POST.
  const selectedCanaux = useMemo(() => {
    const visibleIds = new Set(
      visibleInboxChannels(reservation).map((c) => c.id),
    );
    return Object.entries(selected)
      .filter(([k, v]) => v && visibleIds.has(k))
      .map(([k]) => k);
  }, [selected, reservation]);

  function toggleCanal(id) {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  async function send() {
    if (envoiEnCoursRef.current) return;
    if (!body.trim()) {
      setError("Message vide");
      return;
    }
    if (selectedCanaux.length === 0) {
      setError("Choisissez au moins un canal");
      return;
    }
    const bodyTrimmed = body.trim();
    envoiEnCoursRef.current = true;
    setSending(true);
    setError(null);
    setSentAt(null);
    setResults(null);
    try {
      // B1 (sécu) — `inbox_reply` est désormais gardé `requireAdmin` côté serveur.
      // On joint le Bearer de la session admin au POST (sinon 401 fail-closed).
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setError("Pas de session admin active");
        setSending(false);
        return;
      }

      // Threading email : Message-ID original si le dernier inbound était email.
      const lastInboundIsEmail = lastInbound?.canal === "email";
      const inReplyToMessageId = lastInboundIsEmail
        ? lastInbound?.metadata?.message_id ||
          lastInbound?.metadata?.in_reply_to ||
          null
        : null;

      // Sujet : pertinent uniquement si email coché ET inbound email.
      const subject =
        selectedCanaux.includes("email") &&
        lastInboundIsEmail &&
        lastInbound?.subject
          ? lastInbound.subject.startsWith("Re: ")
            ? lastInbound.subject
            : "Re: " + lastInbound.subject
          : null;

      if (!cleEnvoiRef.current || cleEnvoiRef.current.texte !== bodyTrimmed) {
        const uuid =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
        cleEnvoiRef.current = { cle: uuid, texte: bodyTrimmed };
      }
      const payload = {
        cleEnvoi: cleEnvoiRef.current.cle,
        threadId,
        canaux: selectedCanaux,
        body: bodyTrimmed,
        subject,
        reservationId: reservation?.id || null,
        pricingBlockId: pricingBlockId || null,
        logementId: reservation?.logement_id || null,
        toEmail: lastInbound?.sender_email || reservation?.guest_email || null,
        beds24BookingId: reservation?.beds24_booking_id || null,
        guestPhone: reservation?.guest_phone || null,
        inReplyToMessageId,
        // LOT D (ADR-249) — brouillon IA d'origine (null si pas de suggestion).
        // Le serveur ne capture que si édité (draft !== body), scrubbé avant insert.
        iaDraft: iaDraftRef.current,
        ...(source ? { source } : {}),
      };

      const res = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type: "inbox_reply", data: payload }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      const list = Array.isArray(json.results) ? json.results : [];
      setResults(list);
      // ⚠️ « ENVOYÉ » = RÉELLEMENT PARTI, et rien d'autre. Un `sent_dry_run`
      // (mode essai) n'a rien envoyé au voyageur : il n'allume PAS la coche
      // verte — sa ligne par canal dit « simulé (mode essai) ».
      const partis = list.filter((r) => r.status === "sent");
      const simules = list.filter((r) => r.status === "sent_dry_run");
      const echecs = list.filter((r) => r.status === "failed");
      if (partis.length > 0) setSentAt(new Date());
      if (echecs.length > 0) {
        // Un échec se voit et se RENVOIE D'UN GESTE : le texte reste, seuls
        // les canaux en échec restent cochés (jamais un renvoi sur un canal
        // déjà parti — ce serait un double envoi).
        const enEchec = echecs.map((r) => r.canal);
        setCanauxEnEchec(enEchec);
        setSelected(Object.fromEntries(enEchec.map((c) => [c, true])));
      } else {
        setCanauxEnEchec([]);
        if (partis.length + simules.length > 0) {
          setBody("");
          cleEnvoiRef.current = null; // message traité : le suivant aura sa clé
        }
      }
      // H-2 — propage le détail au caller pour optimistic-insert.
      onSent &&
        onSent({ results: list, canaux: selectedCanaux, body: bodyTrimmed });
    } catch (e) {
      setError(e.message);
    } finally {
      envoiEnCoursRef.current = false;
      setSending(false);
    }
  }

  if (!canReply) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          padding: "10px 16px",
          borderTop: `1px solid ${DS.border}`,
          background: DS.warningBg,
          fontSize: 11,
          color: DS.warningText,
        }}
      >
        <AlertTriangle size={13} strokeWidth={2} style={{ flexShrink: 0 }}/> Aucun canal disponible pour répondre (coordonnées voyageur
        manquantes).
      </div>
    );
  }

  const noCanal = selectedCanaux.length === 0;
  const disableSend = sending || !body.trim() || noCanal;

  return (
    <div
      style={{
        padding: "10px 14px",
        borderTop: `1px solid ${DS.border}`,
        background: DS.card,
      }}
    >
      {/* Cases à cocher des canaux (rendues depuis le registre) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 8,
          fontSize: 11,
          color: DS.textSecondary,
          flexWrap: "wrap",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Reply size={13} strokeWidth={2}/> Répondre via</span>
        {/* Gating plateforme : résa airbnb/booking → Chat OTA seul (les alias
            OTA rendent Mail/SMS inutilisables) ; sinon registre complet. */}
        {visibleInboxChannels(reservation).map((channel) => {
          const coordAvailable = hasCoordinate(
            channel,
            reservation,
            lastInbound,
          );
          // WAGON N — actionnable = enabled OU câblé Inbox (SMS). WhatsApp non.
          const actionable = isInboxActionable(channel.id);
          const disabled = !actionable || !coordAvailable;
          const checked = Boolean(selected[channel.id]);
          // WAGON N — WhatsApp NON retenu : grisé SANS promesse « bientôt ».
          const title = !actionable
            ? "Canal non disponible"
            : !coordAvailable
              ? "Coordonnée voyageur manquante pour ce canal"
              : undefined;
          return (
            <label
              key={channel.id}
              data-testid={`canal-checkbox-${channel.id}`}
              title={title}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.45 : 1,
                fontWeight: checked ? 700 : 400,
                color: checked ? DS.marine : DS.textSecondary,
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggleCanal(channel.id)}
              />
              {channel.label}
              {!actionable && (
                <span
                  style={{
                    fontSize: 9,
                    padding: "1px 4px",
                    background: "#eef2f7",
                    borderRadius: 3,
                    marginLeft: 2,
                    color: "#999",
                  }}
                >
                  indisponible
                </span>
              )}
            </label>
          );
        })}
        {sentAt && (
          <span data-testid="envoye-a" style={{ display: "inline-flex", alignItems: "center", gap: 3, color: DS.success, fontWeight: 600 }}>
            <Check size={12} strokeWidth={2.4}/>{" "}
            {sentAt.toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
      </div>

      {/* Chantier 12 LOT 0 — Copilote IA : suggérer un brouillon ÉPHÉMÈRE. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className="fbz-press"
          data-testid="ia-suggest-btn"
          onClick={suggest}
          disabled={suggesting || sending}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: DS.catIndigoBg,
            color: DS.catIndigoIco,
            border: "none",
            borderRadius: DS.radiusControl,
            padding: "6px 13px",
            fontSize: 11,
            fontWeight: 700,
            cursor: suggesting || sending ? "not-allowed" : "pointer",
            opacity: suggesting || sending ? 0.55 : 1,
            fontFamily: DS.fontBody,
          }}
        >
          <Sparkles size={13} strokeWidth={2}/> {suggesting ? "Génération…" : "Suggérer une réponse"}
        </button>
        {suggestError && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: DS.dangerText, fontWeight: 600 }}>
            <AlertTriangle size={12} strokeWidth={2}/> {suggestError}
          </span>
        )}
        {iaDisabled && !suggestError && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: DS.textMuted, fontWeight: 600 }}>
            <Pause size={12} strokeWidth={2}/> Copilote désactivé pour ce logement.
          </span>
        )}
        {preambleBlocked && !suggestError && !iaDisabled && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: DS.warningText, fontWeight: 600 }}>
            <AlertTriangle size={12} strokeWidth={2}/> Brouillon non exploitable, réessayez.
          </span>
        )}
      </div>

      {/* LOT 1 — Banner d'escalade « à traiter par toi » (informatif, ne bloque
          JAMAIS l'envoi manuel : copilote = l'humain valide tout). */}
      {escalation?.escalated && (
        <div
          data-testid="ia-escalation-banner"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            background: DS.warningBg,
            border: `1px solid ${DS.warning}`,
            borderLeft: `3px solid ${DS.warning}`,
            borderRadius: 7,
            padding: "7px 10px",
            marginBottom: 8,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: DS.warningText, fontWeight: 700 }}>
            <AlertTriangle size={12} strokeWidth={2}/> À traiter par vous — {reasonLabelFR(escalation.reasonCode)}
          </span>
          {!escalation.hasDraft && (
            <span style={{ fontSize: 10, color: "#9a6a12" }}>
              Pas de brouillon proposé : rédigez la réponse à la main.
            </span>
          )}
          {escalation.capturable && (
            <span style={{ fontSize: 10, color: "#9a6a12" }}>
              Vous pouvez enregistrer la réponse comme FAQ apprise depuis l'onglet
              IA du logement.
            </span>
          )}
        </div>
      )}

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Votre réponse…"
        rows={3}
        disabled={sending}
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: `0.5px solid ${DS.border}`,
          borderRadius: DS.radiusControl,
          padding: "8px 12px",
          fontSize: 13,
          outline: "none",
          background: DS.card,
          color: DS.textPrimary,
          fontFamily: DS.fontBody,
          resize: "vertical",
          lineHeight: 1.5,
        }}
      />

      {canauxEnEchec.length > 0 && !sending && (
        <div
          data-testid="echec-renvoyer"
          style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: 11, color: DS.dangerText, fontWeight: 700 }}
        >
          <AlertTriangle size={12} strokeWidth={2}/> Non parti sur{" "}
          {canauxEnEchec
            .map((c) => INBOX_CHANNELS.find((x) => x.id === c)?.label || c)
            .join(", ")}{" "}
          — le texte est gardé : « Renvoyer » le relance sur ce canal seulement.
        </div>
      )}

      {/* Feedback par canal au retour */}
      {results && results.length > 0 && (
        <div
          data-testid="canal-results"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginTop: 6,
          }}
        >
          {results.map((r, i) => {
            const render = STATUS_RENDER[r.status] || {
              Icon: Info,
              label: r.status,
              color: DS.textSecondary,
            };
            const RIcon = render.Icon;
            const label =
              INBOX_CHANNELS.find((c) => c.id === r.canal)?.label || r.canal;
            return (
              <span
                key={`${r.canal}-${i}`}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: render.color, fontWeight: 600 }}
              >
                <RIcon size={12} strokeWidth={2}/> {label} — {render.label}
                {r.reason ? ` (${r.reason})` : ""}
              </span>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 8,
          gap: 8,
        }}
      >
        {error && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: DS.dangerText, fontWeight: 600 }}>
            <AlertTriangle size={12} strokeWidth={2}/> {error}
          </span>
        )}
        {noCanal && !error && (
          <span style={{ fontSize: 11, color: DS.warningText, fontWeight: 600 }}>
            Sélectionnez au moins un canal
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button
          className="fbz-press"
          onClick={send}
          disabled={disableSend}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: DS.corail,
            color: "#fff",
            border: "none",
            borderRadius: DS.radiusControl,
            padding: "7px 18px",
            fontSize: 12,
            fontWeight: 700,
            cursor: disableSend ? "not-allowed" : "pointer",
            opacity: disableSend ? 0.55 : 1,
            fontFamily: DS.fontBody,
          }}
        >
          <Send size={14} strokeWidth={2}/>{" "}
          {sending ? "Envoi…" : canauxEnEchec.length > 0 ? "Renvoyer" : "Envoyer"}
        </button>
      </div>
    </div>
  );
}
